<script setup lang="ts">
import { Head, Link, router } from "@inertiajs/vue3"
import { Search, SlidersHorizontal, Users, X } from "lucide-vue-next"
import { Pencil } from "lucide-vue-next"
import { computed, onUnmounted, ref, watch } from "vue"

import AppDataTable from "@/components/AppDataTable.vue"
import type { TableColumn } from "@/components/AppDataTable.vue"
import AppPagination from "@/components/AppPagination.vue"
import RelativeTime from "@/components/RelativeTime.vue"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/AppLayout.vue"
import { formatDate } from "@/lib/utils"
import {
  customersPath,
  editCustomerPath,
} from "@/routes"
import { type BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  first_name: string
  last_name: string
  middle_name: string | null
  email: string
  phone: string
  on_hold: boolean
  date_of_birth: string
  created_at: string
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  on_hold?: string | null
  sort?: string | null
  direction?: string | null
}

const props = defineProps<{
  customers: Customer[]
  pagination: PaginationMeta
  filters: Filters
}>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Customers", href: customersPath() },
]

const ALL = "__all__"

const search = ref(props.filters.q ?? "")
const onHoldFilter = ref(props.filters.on_hold ?? ALL)
const sortCol = ref(props.filters.sort ?? "")
const sortDir = ref<"asc" | "desc">((props.filters.direction as "asc" | "desc") ?? "desc")

const hasActiveFilters = computed(
  () => search.value !== "" || onHoldFilter.value !== ALL,
)

const filterParams = computed(() => ({
  q: search.value || undefined,
  on_hold: onHoldFilter.value !== ALL ? onHoldFilter.value : undefined,
  sort: sortCol.value || undefined,
  direction: sortCol.value ? sortDir.value : undefined,
}))

const tableColumns: TableColumn[] = [
  { key: "name", label: "Name", sortable: true },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "date_of_birth", label: "Date of Birth" },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Created", sortable: true },
  { key: "actions", label: "Actions", align: "right" },
]

let searchTimer: ReturnType<typeof setTimeout>

function applyFilters() {
  const params = Object.fromEntries(
    Object.entries(filterParams.value).filter(([, v]) => v != null),
  )
  router.get(customersPath(), params, { preserveState: true, replace: true })
}

onUnmounted(() => clearTimeout(searchTimer))

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})

watch([onHoldFilter], applyFilters)

function clearFilters() {
  search.value = ""
  onHoldFilter.value = ALL
  sortCol.value = ""
  sortDir.value = "desc"
}

function handleSort(key: string, dir: "asc" | "desc") {
  sortCol.value = key
  sortDir.value = dir
  applyFilters()
}

function fullName(customer: Customer) {
  return [customer.first_name, customer.middle_name, customer.last_name].filter(Boolean).join(" ")
}
</script>

<template>
  <Head title="Customers" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <!-- Page header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Users class="text-muted-foreground size-6" />
          <div>
            <h1 class="text-foreground text-xl font-semibold">Customers</h1>
            <p class="text-muted-foreground text-sm">View and manage customers</p>
          </div>
        </div>
      </div>

      <!-- Table card -->
      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">All Customers</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} customer{{
                  pagination.total_count !== 1 ? "s" : ""
                }}{{ hasActiveFilters ? " found" : " total" }}
              </CardDescription>
            </div>
            <SlidersHorizontal class="text-muted-foreground size-4" />
          </div>
        </CardHeader>

        <!-- Search & filters -->
        <div class="border-border border-b px-4 pb-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <!-- Search -->
            <div class="relative flex-1">
              <Search class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                v-model="search"
                placeholder="Search by name, email or phone…"
                class="pl-9"
              />
            </div>

            <!-- On hold filter -->
            <Select v-model="onHoldFilter">
              <SelectTrigger class="w-full sm:w-40">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="ALL">Any status</SelectItem>
                <SelectItem value="false">Active</SelectItem>
                <SelectItem value="true">On Hold</SelectItem>
              </SelectContent>
            </Select>

            <!-- Clear filters -->
            <Button
              v-if="hasActiveFilters"
              variant="ghost"
              size="sm"
              class="text-muted-foreground hover:text-foreground shrink-0"
              @click="clearFilters"
            >
              <X class="size-4" />
              Clear
            </Button>
          </div>
        </div>

        <CardContent class="p-0">
          <!-- Empty state -->
          <div
            v-if="customers.length === 0"
            class="flex flex-col items-center justify-center gap-3 py-16 text-center"
          >
            <Users class="text-muted-foreground/40 size-12" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? "No customers match your filters" : "No customers yet" }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable
            v-else
            :columns="tableColumns"
            :sort="sortCol || undefined"
            :direction="sortDir"
            @sort="handleSort"
          >
            <tr
              v-for="customer in customers"
              :key="customer.id"
              class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
            >
              <td class="px-6 py-4 font-medium">
                <Link
                  :href="editCustomerPath(customer.id)"
                  class="text-foreground hover:text-primary hover:underline"
                >{{ fullName(customer) }}</Link>
              </td>
              <td class="text-muted-foreground px-6 py-4">{{ customer.email }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ customer.phone }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ formatDate(customer.date_of_birth) }}</td>
              <td class="px-6 py-4">
                <Badge :variant="customer.on_hold ? 'secondary' : 'default'">
                  {{ customer.on_hold ? "On Hold" : "Active" }}
                </Badge>
              </td>
              <td class="text-muted-foreground px-6 py-4">
                <RelativeTime :date="customer.created_at" />
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" class="size-8" as-child>
                    <Link :href="editCustomerPath(customer.id)">
                      <Pencil class="size-4" />
                      <span class="sr-only">Edit</span>
                    </Link>
                  </Button>
                </div>
              </td>
            </tr>
          </AppDataTable>

          <!-- Pagination -->
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="customersPath()"
            :extra-params="filterParams"
          />
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
